/**
 * Data-driven keybinding engine.
 * Replaces the old hard-coded useKeyboardShortcuts hook.
 *
 * Reads keybinding definitions from the store and dispatches
 * matching keyboard/mouse events to action handlers.
 */

import { useEffect, useRef } from 'react';
import { useEditorStore, stepsForType } from '../store/editorStore';
import type { ToolMode } from '../types/beatmap';
import {
  eventToKey,
  eventToMods,
  findMatchingAction,
} from '../config/keybindings';
import { playStepChangeTick } from '../audio/tickSound';

/** Index of the value in `opts` closest to `step` — tolerant of ternary steps
 *  that aren't an exact array member (indexOf would return -1). */
function nearestStepIndex(opts: number[], step: number): number {
  let best = 0, bestDist = Infinity;
  for (let i = 0; i < opts.length; i++) {
    const d = Math.abs(opts[i] - step);
    if (d < bestDist) { bestDist = d; best = i; }
  }
  return best;
}

/**
 * Build a map of action ID → handler function.
 * Called once and memoized; handlers read state imperatively via getState().
 */
function buildActionHandlers(): Record<string, () => void> {
  const gs = () => useEditorStore.getState();

  return {
    // ── Tool Selection ─────────────────────────────────────────
    select_tool:             () => gs().setToolMode('select'),
    eraser_tool:             () => gs().setToolMode('eraser'),
    select_left_hand:        () => gs().setToolMode('note_left'),
    select_right_hand:       () => gs().setToolMode('note_right'),
    select_onehand_special:  () => gs().setToolMode('note_onehand'),
    select_twohand_special:  () => gs().setToolMode('note_bothhands'),

    // ── Playback ───────────────────────────────────────────────
    toggle_play:         () => gs().setPlaying(!gs().playback.playing),
    toggle_play_return:  () => {
      const s = gs();
      if (s.playback.playing) {
        s.setPlaying(false);
        // Return to the position where play started (would need stored pos)
      } else {
        s.setPlaying(true);
      }
    },

    // ── Selection ──────────────────────────────────────────────
    select_all:          () => gs().selectAll(),
    delete_selected:     () => {
      const s = gs();
      // While the Shapes panel is open, Delete removes the selected decor
      // group(s) (click- or box-selected) instead of notes.
      if (s.shapeDesignerOpen && s.selectedDesignShapeIds.length) {
        s.removeDesignShapes(s.selectedDesignShapeIds);
        return;
      }
      // Delete selected notes AND selected regular obstacles (walls) together.
      const noteIds = s.selectedNoteIds;
      const slideIds = s.selectedSlideIds;
      if (slideIds.length > 0) s.removeSlides([...slideIds]);
      if (noteIds.length > 0) s.removeNotes([...noteIds]);
      // Nothing selected → Delete removes the note under the cursor (reviewer
      // ask: DEL should work at the editor plane without area-selecting first).
      if (noteIds.length === 0 && slideIds.length === 0 && s.hoveredNoteIndex !== null) {
        s.removeNotes([s.hoveredNoteIndex]);
      }
    },

    // ── Clipboard ──────────────────────────────────────────────
    // While the Shapes panel is open it OWNS clipboard/undo so you can
    // copy/paste/undo decor groups; otherwise these operate on notes as usual.
    copy:                () => {
      const s = gs();
      if (s.shapeDesignerOpen && s.selectedDesignShapeIds.length) s.copyDesignShape();
      else s.copySelected();
    },
    cut:                 () => gs().cutSelected(),
    paste:               () => {
      const s = gs();
      if (s.shapeDesignerOpen && s.designClipboard) s.pasteDesignShape();
      else s.pasteAtBeat(s.playback.currentBeat);
    },
    paste_mirror:        () => {
      const s = gs();
      gs().pasteMirrorAtBeat(s.playback.currentBeat);
    },

    // ── Undo/Redo ──────────────────────────────────────────────
    undo:                () => {
      const s = gs();
      if (s.shapeDesignerOpen && s.designUndoStack.length) s.undoDesign();
      else s.undo();
    },
    redo:                () => {
      const s = gs();
      if (s.shapeDesignerOpen && s.designRedoStack.length) s.redoDesign();
      else s.redo();
    },

    // ── Note Editing ───────────────────────────────────────────
    flip_object:         () => gs().mirrorSelected(),
    flip_object_y:       () => gs().flipSelectedY(),
    duplicate_opposite:  () => gs().duplicateSelectedOppositeHand(),
    scale_selection_expand:   () => gs().scaleSelection(2),
    scale_selection_compress: () => gs().scaleSelection(0.5),
    toggle_mirror_mode:  () => gs().toggleMirrorMode(),
    mirror_invert_y:     () => gs().toggleMirrorInvertY(),
    join_notes_rail:     () => gs().joinNotesToRail(),
    split_rail:          () => gs().splitRailAtNode(),
    recalc_rail:         () => gs().recalcRailNodesByStep(),
    wall_left:           () => gs().placeWallAtPlayhead('wall_left'),
    wall_right:          () => gs().placeWallAtPlayhead('wall_right'),
    wall_center:         () => gs().placeWallAtPlayhead('center'),
    wall_diag_left:      () => gs().placeWallAtPlayhead('angle_left'),
    wall_diag_right:     () => gs().placeWallAtPlayhead('angle_right'),
    wall_crouch:         () => gs().placeWallAtPlayhead('crouch'),
    wall_square:         () => gs().placeWallAtPlayhead('square'),
    wall_triangle:       () => gs().placeWallAtPlayhead('triangle'),
    add_note_playing:    () => gs().placeNoteAtPlayhead(),
    toggle_same_note_overwrite: () => gs().toggleSameNoteOverwrite(),
    snip_rail:           () => gs().snipRailAtPlayhead(),
    cycle_note_type:     () => gs().cycleSelectedNoteType(),
    snap_selection_to_rail: () => gs().snapSelectionToRail(),
    delete_all:          () => gs().deleteAllObjects(),
    clear_bookmarks:     () => gs().clearBookmarks(),
    toggle_long_note:    () => {
      const s = gs();
      const mode = s.toolMode;
      // Toggle between note and rail mode for the current hand
      const toggleMap: Record<string, ToolMode> = {
        note_left: 'rail_left', rail_left: 'note_left',
        note_right: 'rail_right', rail_right: 'note_right',
        note_onehand: 'rail_onehand', rail_onehand: 'note_onehand',
        note_bothhands: 'rail_bothhands', rail_bothhands: 'note_bothhands',
      };
      if (toggleMap[mode]) s.setToolMode(toggleMap[mode]);
    },

    // ── Timeline ───────────────────────────────────────────────
    move_forward:        () => {
      const s = gs();
      const step = s.scrollStep;
      const newBeat = s.playback.currentBeat + step;
      const bpm = s.beatmap?.bpm || 120;
      const offset = s.beatmap?.offset || 0;
      const time = (newBeat / (bpm / 60)) + (offset / 1000);
      s.setCurrentTime(Math.max(0, time));
    },
    move_backward:       () => {
      const s = gs();
      const step = s.scrollStep;
      const newBeat = s.playback.currentBeat - step;
      const bpm = s.beatmap?.bpm || 120;
      const offset = s.beatmap?.offset || 0;
      const time = (newBeat / (bpm / 60)) + (offset / 1000);
      s.setCurrentTime(Math.max(0, time));
    },
    timeline_start:      () => gs().setCurrentTime(0),
    timeline_end:        () => gs().setCurrentTime(gs().playback.duration),

    // ── Beat Step ──────────────────────────────────────────────
    beat_step_down:      () => {
      const opts = stepsForType(gs().stepType);
      const idx = nearestStepIndex(opts, gs().scrollStep);
      if (idx > 0) {
        gs().setScrollStep(opts[idx - 1]);
        playStepChangeTick('down');
      }
    },
    beat_step_up:        () => {
      const opts = stepsForType(gs().stepType);
      const idx = nearestStepIndex(opts, gs().scrollStep);
      if (idx < opts.length - 1) {
        gs().setScrollStep(opts[idx + 1]);
        playStepChangeTick('up');
      }
    },
    cycle_step_type:     () => gs().cycleStepType(),

    // ── Interface ──────────────────────────────────────────────
    open_settings:       () => gs().setSettingsOpen(!gs().settingsOpen),
    open_file_manager:   () => gs().setFileManagerOpen(!gs().fileManagerOpen),
    new_map:             () => gs().setNewMapOpen(!gs().newMapOpen),
    toggle_keybindings_panel: () => gs().setKeybindingsOpen(!gs().keybindingsOpen),
    toggle_grid_guide:   () => gs().setGridGuideVisible(!gs().gridGuideVisible),
    cycle_grid_guide:    () => {
      const types: Array<'solid' | 'outline' | 'circles'> = ['solid', 'outline', 'circles'];
      const current = gs().gridGuideType;
      const idx = types.indexOf(current);
      gs().setGridGuideType(types[(idx + 1) % types.length]);
    },
    toggle_snap_to_grid: () => gs().setSnapToGrid(!gs().snapToGrid),
    toggle_sidebars:     () => {
      const s = gs();
      const allOpen = s.leftSidebarOpen && s.rightSidebarOpen;
      s.setLeftSidebarOpen(!allOpen);
      s.setRightSidebarOpen(!allOpen);
    },
    toggle_metronome:    () => gs().setMetronomeEnabled(!gs().metronomeEnabled),

    // ── Camera ─────────────────────────────────────────────────
    camera_center:       () => gs().setCameraView('default'),
    camera_left:         () => gs().setCameraView('left'),
    camera_right:        () => gs().setCameraView('right'),
    camera_free:         () => gs().setCameraView('free'),

    // ── Volume ─────────────────────────────────────────────────
    decrease_music_vol:  () => gs().setMusicVolume(Math.max(0, gs().musicVolume - 0.05)),
    increase_music_vol:  () => gs().setMusicVolume(Math.min(1, gs().musicVolume + 0.05)),
    decrease_sfx_vol:    () => gs().setSfxVolume(Math.max(0, gs().sfxVolume - 0.05)),
    increase_sfx_vol:    () => gs().setSfxVolume(Math.min(1, gs().sfxVolume + 0.05)),

    // ── Save ───────────────────────────────────────────────────
    save_map:            () => {
      // Trigger the download/save flow — dispatch a custom event
      window.dispatchEvent(new CustomEvent('abm-save-map'));
    },

    // ── Bookmarks / Flash ────────────────────────────────────────
    toggle_bookmark:     () => {
      const s = gs();
      const beat = s.playback.currentBeat;
      const existing = s.bookmarks.find(b => Math.abs(b.beat - beat) < 0.01);
      if (existing) {
        s.removeBookmark(beat);
      } else {
        s.addBookmark(beat, '');
      }
    },
    toggle_flash:        () => {
      const s = gs();
      const beat = s.playback.currentBeat;
      const existing = s.flashEvents.find(e => Math.abs(e.time_beats - beat) < 0.01);
      if (existing) {
        s.removeFlashEvent(beat);
      } else {
        // New light = normal single pulse; strobe is opt-in per light (popup).
        s.addFlashEvent({ ...s.flashDefaults, time_beats: beat, repeat: 1 });
      }
    },

    // ── Area selection (placeholder) ───────────────────────────
    area_selection:      () => { /* TODO: toggle area selection mode */ },

    // ── These are mouse-action keybindings handled by the 3D scene, not here:
    // place_note, drag_snap_object, snap_grid_to_object, remote_delete,
    // rotate_wall_cw, rotate_wall_ccw, add_node_to_rail, change_note_color
  };
}

// ── Main Hook ────────────────────────────────────────────────────────────────

export default function useKeybindingEngine() {
  const handlersRef = useRef<Record<string, () => void>>(buildActionHandlers());

  useEffect(() => {
    // ── Ctrl + mouse wheel → cycle step size ───────
    const handleWheel = (e: WheelEvent) => {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      const st = useEditorStore.getState();
      const opts = stepsForType(st.stepType);
      const idx = nearestStepIndex(opts, st.scrollStep);
      // The step arrays run fine → coarse, so a FINER grid is a LOWER index.
      // Scrolling up must subdivide (1/4 → 1/8), matching the desktop editor and
      // the usual zoom-in convention; it used to do the opposite.
      const dir = e.deltaY < 0 ? -1 : 1;
      const next = idx + dir;
      if (next >= 0 && next < opts.length) {
        st.setScrollStep(opts[next]);
        // Tick follows the gesture: scrolling up reads as "up".
        playStepChangeTick(dir < 0 ? 'up' : 'down');
      }
    };

    const handleKey = (e: KeyboardEvent) => {
      // Suppress shortcuts ONLY while the user is genuinely typing TEXT — a text
      // <input>, a <textarea>, or a contentEditable region. Everything else that
      // can hold focus but doesn't take typed text (a <select> dropdown, any
      // <button>, colour/checkbox/radio/range inputs — all over the Shape Designer)
      // must NOT swallow shortcuts: otherwise, once one keeps focus after a click,
      // Space (play/pause) and the rest stay dead until you click empty space.
      // For those focused controls we still fire the shortcut but preventDefault
      // below so Space doesn't ALSO activate the focused button / open the select.
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName;
      // A numeric slider/stepper readout (marked data-num-readout): let plain
      // keys TYPE normally, but still fire Ctrl/Cmd shortcuts (undo/redo/copy/
      // paste) — otherwise, once a readout keeps focus, Ctrl+Z can't undo the
      // decor edit until you click away.
      const isNumReadout = tag === 'INPUT' && (target as HTMLInputElement).dataset?.numReadout != null;
      if (isNumReadout) {
        if (!(e.ctrlKey || e.metaKey)) return;   // plain key → type it, no shortcut
        // Ctrl/Cmd combo → fall through to shortcut matching (Ctrl+Z etc.)
      } else {
        // Suppress shortcuts while genuinely typing TEXT — a text <input>, a
        // <textarea>, or contentEditable. Everything else that can hold focus but
        // doesn't take typed text (a <select>, any <button>, colour/checkbox/range
        // inputs — all over the Shape Designer) must NOT swallow shortcuts.
        const isTyping =
          tag === 'TEXTAREA' ||
          !!target?.isContentEditable ||
          (tag === 'INPUT' &&
            !['color', 'checkbox', 'radio', 'range', 'button', 'submit', 'reset', 'file', 'image'].includes(
              (target as HTMLInputElement).type));
        if (isTyping) return;
      }

      // Suppress WASDQE when fly mode is active
      if (useEditorStore.getState().isFlyModeActive) {
        const k = e.key.toUpperCase();
        if (['W', 'A', 'S', 'D', 'Q', 'E'].includes(k)) return;
      }

      // If the keybindings rebind dialog is listening, don't dispatch
      if (useEditorStore.getState().keybindingsOpen) {
        // Only allow Escape to close the keybindings panel
        if (e.key === 'Escape') {
          useEditorStore.getState().setKeybindingsOpen(false);
          return;
        }
      }

      // ── Drag in progress: Escape cancels ─────────────────────
      if (useEditorStore.getState().draggingNoteIndex !== null) {
        if (e.key === 'Escape') {
          e.preventDefault();
          useEditorStore.getState().cancelDrag();
          return;
        }
      }

      // ── Rail placement: Enter finishes, Escape cancels ──────
      if (useEditorStore.getState().railPlacementActive) {
        if (e.key === 'Enter') {
          e.preventDefault();
          useEditorStore.getState().finishRailPlacement();
          return;
        }
        if (e.key === 'Escape') {
          e.preventDefault();
          useEditorStore.getState().cancelRailPlacement();
          return;
        }
      }

      // Convert event to our normalized key + mods
      const key = eventToKey(e);
      const mods = eventToMods(e);

      // Find matching action
      const bindings = useEditorStore.getState().keybindings;
      const actionId = findMatchingAction(bindings, key, mods);

      if (actionId) {
        const handler = handlersRef.current[actionId];
        if (handler) {
          e.preventDefault();
          handler();
        }
      }
    };

    // ── Ctrl hold → temporary select mode ──────────────────────
    let toolBeforeCtrl: ToolMode | null = null;

    const handleKeyUp = (e: KeyboardEvent) => {
      if ((e.key === 'Control' || e.key === 'Meta') && toolBeforeCtrl !== null) {
        // Restore previous tool — temporary switch so don't finish rail placement
        useEditorStore.getState().setToolMode(toolBeforeCtrl, true);
        toolBeforeCtrl = null;
      }
    };

    const handleKeyDownWithCtrl = (e: KeyboardEvent) => {
      // Ctrl press (alone, not as part of a combo with another key)
      if ((e.key === 'Control' || e.key === 'Meta') && toolBeforeCtrl === null) {
        const store = useEditorStore.getState();
        if (store.toolMode !== 'select') {
          toolBeforeCtrl = store.toolMode;
          // Temporary switch — don't finish rail placement
          store.setToolMode('select', true);
        }
      }
    };

    window.addEventListener('keydown', handleKey);
    window.addEventListener('keydown', handleKeyDownWithCtrl);
    window.addEventListener('keyup', handleKeyUp);
    window.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      window.removeEventListener('keydown', handleKey);
      window.removeEventListener('keydown', handleKeyDownWithCtrl);
      window.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('wheel', handleWheel);
    };
  }, []);
}
